import { getGatewayBaseUrl } from "./gateway";

export interface EnvironmentConfig {
  NODE_ENV: string;
  MODE: string;
  DEV: boolean;
  PROD: boolean;
  TEST: boolean;
  API_BASE_URL: string;
  FRONTEND_URL: string;
  SMART_OPS_FRONTEND_PORT: string;
  SMART_OPS_BACKEND_PORT: string;
  API_TIMEOUT: number;
  API_RETRY_ATTEMPTS: number;
  API_RETRY_DELAY: number;
  TEST_TIMEOUT: number;
  TEST_RETRIES: number;
  IAM_MODE: string;
  KEYCLOAK_URL: string;
  KEYCLOAK_REALM: string;
  KEYCLOAK_CLIENT_ID: string;
  TOKEN: string;
  ENABLE_ANALYTICS: boolean;
  ENABLE_MONITORING: boolean;
  LOG_LEVEL: string;
  ENABLE_DEBUG_MODE: boolean;
  ENABLE_HOT_RELOAD: boolean;
  AI_MAX_TOKENS: number;
  DATABASE_DEFAULT_PORT: number;
}

const viteEnv = import.meta.env as Record<string, string | boolean | undefined>;

const getEnvVar = (key: string): string | undefined => {
  const value = key.startsWith("VITE_") ? viteEnv[key] : viteEnv[`VITE_${key}`] ?? viteEnv[key];
  return typeof value === "string" ? value : undefined;
};

const firstValue = (...values: Array<string | undefined>): string => values.find((value) => value !== undefined) ?? "";
const parseEnvInt = (value: string | undefined, defaultValue: string): number => parseInt(value ?? defaultValue, 10);
const isEnabled = (value: string | undefined, defaultValue: boolean): boolean => (value === undefined ? defaultValue : value === "true");

const getEnvironmentConfig = (): EnvironmentConfig => {
  const mode = String(viteEnv.MODE ?? "development");
  const apiBaseUrl = getEnvVar("VITE_API_BASE_URL") ?? "";
  const dev = mode === "development";
  const prod = mode === "production";
  const test = mode === "test";

  return {
    NODE_ENV: mode,
    MODE: mode,
    DEV: dev,
    PROD: prod,
    TEST: test,
    API_BASE_URL: apiBaseUrl,
    FRONTEND_URL: firstValue(getEnvVar("VITE_FRONTEND_URL"), getEnvVar("SMART_OPS_FRONTEND_URL")),
    SMART_OPS_FRONTEND_PORT: firstValue(getEnvVar("VITE_SMART_OPS_FRONTEND_PORT"), getEnvVar("SMART_OPS_FRONTEND_PORT")),
    SMART_OPS_BACKEND_PORT: firstValue(getEnvVar("VITE_SMART_OPS_BACKEND_PORT"), getEnvVar("SMART_OPS_BACKEND_PORT")),
    API_TIMEOUT: parseEnvInt(getEnvVar("VITE_API_TIMEOUT"), "30000"),
    API_RETRY_ATTEMPTS: parseEnvInt(getEnvVar("VITE_API_RETRY_ATTEMPTS"), "3"),
    API_RETRY_DELAY: parseEnvInt(getEnvVar("VITE_API_RETRY_DELAY"), "1000"),
    TEST_TIMEOUT: parseEnvInt(getEnvVar("VITE_TEST_TIMEOUT"), "10000"),
    TEST_RETRIES: parseEnvInt(getEnvVar("VITE_TEST_RETRIES"), "1"),
    IAM_MODE: getEnvVar("VITE_IAM_MODE") ?? "mock",
    KEYCLOAK_URL: getEnvVar("VITE_KEYCLOAK_URL") ?? "",
    KEYCLOAK_REALM: getEnvVar("VITE_KEYCLOAK_REALM") ?? "9s-realm",
    KEYCLOAK_CLIENT_ID: getEnvVar("VITE_KEYCLOAK_CLIENT_ID") ?? "9s-client",
    TOKEN: getEnvVar("VITE_TOKEN") ?? "default_token",
    ENABLE_ANALYTICS: isEnabled(getEnvVar("VITE_ENABLE_ANALYTICS"), dev),
    ENABLE_MONITORING: isEnabled(getEnvVar("VITE_ENABLE_MONITORING"), dev),
    LOG_LEVEL: getEnvVar("VITE_LOG_LEVEL") ?? (dev ? "debug" : "info"),
    ENABLE_DEBUG_MODE: isEnabled(getEnvVar("VITE_ENABLE_DEBUG_MODE"), dev),
    ENABLE_HOT_RELOAD: isEnabled(getEnvVar("VITE_ENABLE_HOT_RELOAD"), dev),
    AI_MAX_TOKENS: parseEnvInt(getEnvVar("VITE_AI_MAX_TOKENS"), "4000"),
    DATABASE_DEFAULT_PORT: parseEnvInt(getEnvVar("VITE_DATABASE_DEFAULT_PORT"), "5432"),
  };
};

export const env = getEnvironmentConfig();

if (env.DEV) {
  console.debug("Environment loaded", {
    mode: env.MODE,
    apiGatewayUrl: env.API_BASE_URL,
  });
}

export const validateEnvironment = (): boolean => {
  const requiredUrls = [env.API_BASE_URL];
  const missingUrls = requiredUrls.filter((url) => !url);
  if (missingUrls.length > 0) {
    console.error("Missing required environment variables:", missingUrls);
    return false;
  }
  if (env.DEV) {
    console.debug("Environment validation passed");
  }
  return true;
};

export const isDevelopment = (): boolean => env.DEV;
export const isProduction = (): boolean => env.PROD;
export const isTest = (): boolean => env.TEST;

export const getServiceUrl = (): string => getGatewayBaseUrl();

export const getApiUrl = (endpoint: string): string => {
  const path = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  return `${getGatewayBaseUrl()}${path}`;
};

export const getTestConfig = () => ({
  enableDebugMode: env.ENABLE_DEBUG_MODE,
  logLevel: env.LOG_LEVEL,
});

export const getMonitoringConfig = () => ({
  enabled: env.ENABLE_MONITORING,
  logLevel: env.LOG_LEVEL,
  analytics: env.ENABLE_ANALYTICS,
});

export const getAuthConfig = () => ({
  mode: env.IAM_MODE,
  keycloak: {
    url: env.KEYCLOAK_URL,
    realm: env.KEYCLOAK_REALM,
    clientId: env.KEYCLOAK_CLIENT_ID,
  },
  token: env.TOKEN,
});

export const isKeycloakMode = (): boolean => {
  const m = env.IAM_MODE;
  return ["keycloak", "real"].includes(m);
};

export default env;
